/**
 * Pure edits of a PrintTemplateSpec. Every function returns a NEW spec (or
 * the same object when nothing changed, so the store can skip no-ops).
 */

import { compose, rotate, translate, apply, type Matrix } from '@pointorama/print-engine';
import { forEachElement, pageSizeMm, type GroupElement, type PrintPageSpec, type PrintTemplateElement, type PrintTemplateSpec } from 'shared-types';

export interface ElementLocation {
  page: PrintPageSpec;
  element: PrintTemplateElement;
  /** Enclosing groups, outermost first. */
  parents: GroupElement[];
}

export function findElement(spec: Pick<PrintTemplateSpec, 'pages'>, id: string): ElementLocation | null {
  for (const page of spec.pages) {
    const search = (elements: readonly PrintTemplateElement[], parents: GroupElement[]): ElementLocation | null => {
      for (const element of elements) {
        if (element.id === id) return { page, element, parents };
        if (element.kind === 'group') {
          const found = search(element.children, [...parents, element]);
          if (found) return found;
        }
      }
      return null;
    };
    const found = search(page.elements, []);
    if (found) return found;
  }
  return null;
}

/** Every element id in the template (ids are unique template-wide). */
export function allIds(spec: PrintTemplateSpec): Set<string> {
  const ids = new Set<string>();
  for (const page of spec.pages) forEachElement(page.elements, (e) => ids.add(e.id));
  return ids;
}

/** `base`, `base2`, `base3`… the first id not used anywhere in the template. */
export function uniqueId(taken: ReadonlySet<string>, base: string): string {
  const clean = base.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 48) || 'el';
  if (!taken.has(clean)) return clean;
  for (let i = 2; ; i++) {
    const candidate = `${clean}${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

export function uniquePageId(spec: PrintTemplateSpec): string {
  return uniqueId(new Set(spec.pages.map((p) => p.id)), 'page');
}

/** Box-local (element) → page coordinates of an element itself (its own box, rotation included). */
export function elementMatrix(element: Pick<PrintTemplateElement, 'xMm' | 'yMm' | 'wMm' | 'hMm' | 'rotationDeg'>): Matrix {
  const { xMm: x, yMm: y, wMm: w, hMm: h, rotationDeg } = element;
  if (!rotationDeg) return translate(x, y);
  return compose(translate(x + w / 2, y + h / 2), rotate(rotationDeg), translate(-w / 2, -h / 2));
}

/** The parent space (page or group content) → page coordinates. */
export function parentMatrix(parents: readonly GroupElement[]): Matrix {
  return compose(...parents.map(elementMatrix));
}

/** Element box-local → page coordinates (through its groups). */
export function worldMatrix(location: Pick<ElementLocation, 'element' | 'parents'>): Matrix {
  return compose(parentMatrix(location.parents), elementMatrix(location.element));
}

/** The four corners of an element's box in page coordinates. */
export function worldCorners(location: Pick<ElementLocation, 'element' | 'parents'>): { x: number; y: number }[] {
  const m = worldMatrix(location);
  const { wMm: w, hMm: h } = location.element;
  return [apply(m, 0, 0), apply(m, w, 0), apply(m, w, h), apply(m, 0, h)];
}

/**
 * The clickable outline of a hollow shape (unfilled rectangle / ellipse,
 * open or unfilled polyline), in page millimeters; null for everything
 * else, which is clickable over its whole box. A frame drawn around the
 * page must not swallow clicks meant for what is inside it.
 */
export function hollowOutline(location: Pick<ElementLocation, 'element' | 'parents'>): { points: { x: number; y: number }[]; closed: boolean } | null {
  const { element } = location;
  const m = worldMatrix(location);
  switch (element.kind) {
    case 'rect':
      return element.fill ? null : { points: worldCorners(location), closed: true };
    case 'ellipse': {
      if (element.fill) return null;
      const { wMm: w, hMm: h } = element;
      const points = Array.from({ length: 48 }, (_, i) => {
        const t = (i / 48) * Math.PI * 2;
        return apply(m, (w / 2) * (1 + Math.cos(t)), (h / 2) * (1 + Math.sin(t)));
      });
      return { points, closed: true };
    }
    case 'polyline':
      return element.closed && element.fill ? null : { points: element.pointsMm.map(([x, y]) => apply(m, x, y)), closed: element.closed };
    default:
      return null;
  }
}

// ─── Element-tree rewriting ─────────────────────────────────────────────

function mapTree(elements: PrintTemplateElement[], fn: (element: PrintTemplateElement) => PrintTemplateElement | null): PrintTemplateElement[] {
  let changed = false;
  const out: PrintTemplateElement[] = [];
  for (const element of elements) {
    let next = fn(element);
    if (next && next.kind === 'group') {
      const children = mapTree(next.children, fn);
      if (children !== next.children) next = { ...next, children };
    }
    if (next !== element) changed = true;
    if (next) out.push(next);
  }
  return changed ? out : elements;
}

function mapPages(spec: PrintTemplateSpec, fn: (page: PrintPageSpec) => PrintPageSpec): PrintTemplateSpec {
  let changed = false;
  const pages = spec.pages.map((page) => {
    const next = fn(page);
    if (next !== page) changed = true;
    return next;
  });
  return changed ? { ...spec, pages } : spec;
}

function mapAllElements(spec: PrintTemplateSpec, fn: (element: PrintTemplateElement) => PrintTemplateElement | null): PrintTemplateSpec {
  return mapPages(spec, (page) => {
    const elements = mapTree(page.elements, fn);
    return elements === page.elements ? page : { ...page, elements };
  });
}

export function updateElement(spec: PrintTemplateSpec, id: string, update: (element: PrintTemplateElement) => PrintTemplateElement): PrintTemplateSpec {
  return mapAllElements(spec, (element) => (element.id === id ? update(element) : element));
}

/** Removes elements; scale bars of a removed viewport go too, north arrows / legends unlink. */
export function removeElements(spec: PrintTemplateSpec, ids: readonly string[]): PrintTemplateSpec {
  const removed = new Set(ids);
  return mapAllElements(spec, (element) => {
    if (removed.has(element.id)) return null;
    if (element.kind === 'scaleBar' && element.viewportId !== null && removed.has(element.viewportId)) return null;
    if ((element.kind === 'northArrow' || element.kind === 'legend') && element.viewportId !== null && removed.has(element.viewportId)) return { ...element, viewportId: null };
    return element;
  });
}

/** Inserts into a page (or a group's children), on top. */
export function insertElement(spec: PrintTemplateSpec, pageId: string, element: PrintTemplateElement, parentId: string | null): PrintTemplateSpec {
  if (parentId) return updateElement(spec, parentId, (g) => (g.kind === 'group' ? { ...g, children: [...g.children, element] } : g));
  return mapPages(spec, (page) => (page.id === pageId ? { ...page, elements: [...page.elements, element] } : page));
}

export type OrderMove = 'forward' | 'backward' | 'front' | 'back';

function reorder<T extends { id: string }>(list: readonly T[], id: string, move: OrderMove): T[] | null {
  const index = list.findIndex((x) => x.id === id);
  if (index < 0) return null;
  const target = move === 'front' ? list.length - 1 : move === 'back' ? 0 : move === 'forward' ? Math.min(list.length - 1, index + 1) : Math.max(0, index - 1);
  if (target === index) return [...list];
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

/** Paint order among the element's siblings (later = on top). */
export function moveInOrder(spec: PrintTemplateSpec, id: string, move: OrderMove): PrintTemplateSpec {
  const location = findElement(spec, id);
  if (!location) return spec;
  const parent = location.parents[location.parents.length - 1];
  if (parent) return updateElement(spec, parent.id, (g) => (g.kind === 'group' ? { ...g, children: reorder(g.children, id, move) ?? g.children } : g));
  return mapPages(spec, (page) => (page.id === location.page.id ? { ...page, elements: reorder(page.elements, id, move) ?? page.elements } : page));
}

/** Deep copy with fresh ids; viewport links inside the copy follow the copied viewports. */
export function cloneWithNewIds(elements: readonly PrintTemplateElement[], taken: Set<string>): PrintTemplateElement[] {
  const remap = new Map<string, string>();
  const assign = (list: readonly PrintTemplateElement[]) => {
    for (const element of list) {
      const id = uniqueId(taken, element.id.replace(/\d+$/, ''));
      taken.add(id);
      remap.set(element.id, id);
      if (element.kind === 'group') assign(element.children);
    }
  };
  assign(elements);
  const rewrite = (element: PrintTemplateElement): PrintTemplateElement => {
    const id = remap.get(element.id) ?? element.id;
    switch (element.kind) {
      case 'group':
        return { ...element, id, children: element.children.map(rewrite) };
      case 'scaleBar':
      case 'northArrow':
      case 'legend':
        return { ...element, id, viewportId: element.viewportId !== null ? (remap.get(element.viewportId) ?? element.viewportId) : null };
      default:
        return { ...element, id };
    }
  };
  return elements.map(rewrite);
}

/** Copies next to the originals (same parent), offset a little; returns the new ids. */
export function duplicateElements(spec: PrintTemplateSpec, ids: readonly string[], offsetMm = 5): { spec: PrintTemplateSpec; ids: string[] } {
  let next = spec;
  const created: string[] = [];
  const taken = allIds(spec);
  for (const id of ids) {
    const location = findElement(next, id);
    if (!location) continue;
    const [copy] = cloneWithNewIds([{ ...location.element, xMm: location.element.xMm + offsetMm, yMm: location.element.yMm + offsetMm }], taken);
    const parent = location.parents[location.parents.length - 1];
    next = insertElement(next, location.page.id, copy, parent?.id ?? null);
    created.push(copy.id);
  }
  return { spec: next, ids: created };
}

/** Groups sibling elements into one group sized to their bounds (positions keep, now group-relative). */
export function groupElements(spec: PrintTemplateSpec, ids: readonly string[]): { spec: PrintTemplateSpec; groupId: string | null } {
  const locations = ids.map((id) => findElement(spec, id)).filter((l): l is ElementLocation => l !== null);
  if (locations.length < 2) return { spec, groupId: null };
  const parentId = locations[0].parents[locations[0].parents.length - 1]?.id ?? null;
  if (locations.some((l) => (l.parents[l.parents.length - 1]?.id ?? null) !== parentId || l.page.id !== locations[0].page.id)) return { spec, groupId: null };
  // Bounds in the parent's coordinates (each element's rotated corners).
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { element } of locations) {
    const m = elementMatrix(element);
    for (const p of [apply(m, 0, 0), apply(m, element.wMm, 0), apply(m, element.wMm, element.hMm), apply(m, 0, element.hMm)]) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }
  const groupId = uniqueId(allIds(spec), 'group');
  const selected = new Set(ids);
  // Children keep paint order.
  const siblings = parentId ? (findElement(spec, parentId)?.element as GroupElement).children : locations[0].page.elements;
  const children = siblings.filter((e) => selected.has(e.id)).map((e) => ({ ...e, xMm: e.xMm - minX, yMm: e.yMm - minY }));
  const group: GroupElement = { id: groupId, name: 'Group', kind: 'group', xMm: minX, yMm: minY, wMm: maxX - minX, hMm: maxY - minY, rotationDeg: 0, children };
  const next = insertElement(removeOnly(spec, ids), locations[0].page.id, group, parentId);
  return { spec: next, groupId };
}

/** Removes elements without touching their dependents (used when moving them). */
function removeOnly(spec: PrintTemplateSpec, ids: readonly string[]): PrintTemplateSpec {
  const removed = new Set(ids);
  return mapAllElements(spec, (element) => (removed.has(element.id) ? null : element));
}

const roundMm = (v: number) => Math.round(v * 100) / 100;

/** Rows a table shows: the header (if on) plus its revision slots or static rows. */
export function tableRowCount(table: Extract<PrintTemplateElement, { kind: 'table' }>): number {
  return (table.showHeader ? 1 : 0) + (table.rows.kind === 'revisions' ? table.rows.maxRows : table.rows.cells.length);
}

/** A table's box made to hold its columns and rows exactly (after column / row edits). */
export function fitTableBox(element: PrintTemplateElement): PrintTemplateElement {
  if (element.kind !== 'table') return element;
  const wMm = roundMm(element.columns.reduce((sum, c) => sum + c.widthMm, 0));
  const hMm = roundMm(tableRowCount(element) * element.rowHeightMm);
  return wMm > 0 && hMm > 0 ? { ...element, wMm, hMm } : element;
}

/**
 * A table edit, keeping its box where the edit changes the structure:
 * adding / removing columns shares the box width out again (in their
 * proportions), a different number of rows shares the box height. Edits of
 * one column's width or of the row height size the box to the content.
 */
export function editTable(previous: PrintTemplateElement, next: PrintTemplateElement): PrintTemplateElement {
  if (previous.kind !== 'table' || next.kind !== 'table') return next;
  const columnsChanged = previous.columns.length !== next.columns.length;
  const rowsChanged = tableRowCount(previous) !== tableRowCount(next);
  if (!columnsChanged && !rowsChanged) return fitTableBox(next);
  let table = next;
  if (columnsChanged) {
    const total = table.columns.reduce((sum, c) => sum + c.widthMm, 0);
    const scale = total > 0 ? previous.wMm / total : 1;
    table = { ...table, columns: table.columns.map((c) => ({ ...c, widthMm: roundMm(c.widthMm * scale) })), wMm: previous.wMm };
  }
  if (rowsChanged) {
    const rows = tableRowCount(table);
    table = { ...table, rowHeightMm: rows > 0 ? roundMm(previous.hMm / rows) : table.rowHeightMm, hMm: previous.hMm };
  }
  return table;
}

// ─── Table edits on the canvas (dividers, add / remove columns and rows) ───

type TableEl = Extract<PrintTemplateElement, { kind: 'table' }>;

/** A table's columns and rows as they sit in its box (local mm): column edges (0 … wMm) and row edges (0 … hMm). */
export function tableGrid(table: TableEl): { xs: number[]; ys: number[] } {
  const natural = table.columns.reduce((sum, c) => sum + c.widthMm, 0);
  const scale = natural > 0 && table.wMm > 0 ? table.wMm / natural : 1;
  const xs = [0];
  for (const column of table.columns) xs.push(xs[xs.length - 1] + column.widthMm * scale);
  const rows = tableRowCount(table);
  const rowH = rows > 0 && table.hMm > 0 ? table.hMm / rows : table.rowHeightMm;
  return { xs, ys: Array.from({ length: rows + 1 }, (_, i) => i * rowH) };
}

/** Narrowest column / lowest row a divider drag leaves (mm). */
const MIN_CELL_MM = 2;

/** Move the divider right of column `j` to `x` (local mm): the two columns beside it trade width, the table keeps its box. */
export function moveTableColumnDivider(table: TableEl, j: number, x: number): TableEl {
  const { xs } = tableGrid(table);
  if (j < 0 || j >= table.columns.length - 1) return table;
  const at = Math.min(xs[j + 2] - MIN_CELL_MM, Math.max(xs[j] + MIN_CELL_MM, x));
  const widths = xs.slice(1).map((edge, i) => edge - xs[i]);
  widths[j] = at - xs[j];
  widths[j + 1] = xs[j + 2] - at;
  return { ...table, columns: table.columns.map((c, i) => ({ ...c, widthMm: roundMm(widths[i]) })) };
}

/** {@link editTable} for tables: a structural edit keeps the box. */
function keepTableBox(previous: TableEl, next: TableEl): TableEl {
  const edited = editTable(previous, next);
  return edited.kind === 'table' ? edited : next;
}

function newColumnId(table: TableEl): string {
  const taken = new Set(table.columns.map((c) => c.id));
  for (let i = table.columns.length + 1; ; i++) if (!taken.has(`c${i}`)) return `c${i}`;
}

/** A new column at index `at` (as wide as its neighbours on average); the box keeps its width. */
export function insertTableColumn(table: TableEl, at: number): TableEl {
  const index = Math.max(0, Math.min(table.columns.length, at));
  const width = table.columns.length > 0 ? table.columns.reduce((sum, c) => sum + c.widthMm, 0) / table.columns.length : table.wMm || 30;
  const column = { id: newColumnId(table), header: 'Column', widthMm: width, cell: '', align: 'left' as const };
  const columns = [...table.columns.slice(0, index), column, ...table.columns.slice(index)];
  const rows: TableEl['rows'] = table.rows.kind === 'static' ? { ...table.rows, cells: table.rows.cells.map((row) => [...row.slice(0, index), '', ...row.slice(index)]) } : table.rows;
  return keepTableBox(table, { ...table, columns, rows });
}

/** Remove column `j` (never the last one); the box keeps its width. */
export function removeTableColumn(table: TableEl, j: number): TableEl {
  if (table.columns.length <= 1 || j < 0 || j >= table.columns.length) return table;
  const rows: TableEl['rows'] = table.rows.kind === 'static' ? { ...table.rows, cells: table.rows.cells.map((row) => row.filter((_, i) => i !== j)) } : table.rows;
  return keepTableBox(table, { ...table, columns: table.columns.filter((_, i) => i !== j), rows });
}

/** A new body row at body index `at` (revision tables: one more revision slot); the box keeps its height. */
export function insertTableRow(table: TableEl, at: number): TableEl {
  const rows: TableEl['rows'] =
    table.rows.kind === 'static'
      ? { ...table.rows, cells: [...table.rows.cells.slice(0, at), table.columns.map(() => ''), ...table.rows.cells.slice(at)] }
      : { ...table.rows, maxRows: table.rows.maxRows + 1 };
  return keepTableBox(table, { ...table, rows });
}

/** Remove body row `i` (never the last one); the box keeps its height. */
export function removeTableRow(table: TableEl, i: number): TableEl {
  if (table.rows.kind === 'static') {
    if (table.rows.cells.length <= 1 || i < 0 || i >= table.rows.cells.length) return table;
    return keepTableBox(table, { ...table, rows: { ...table.rows, cells: table.rows.cells.filter((_, k) => k !== i) } });
  }
  if (table.rows.maxRows <= 1) return table;
  return keepTableBox(table, { ...table, rows: { ...table.rows, maxRows: table.rows.maxRows - 1 } });
}

/**
 * An element at a new box size, its content following: polyline points and
 * table columns / rows stretch, a group's children (title blocks, strips…)
 * move and resize with it, recursively. Text keeps its point size.
 */
export function resizeElementBox(element: PrintTemplateElement, w: number, h: number): PrintTemplateElement {
  const fx = element.wMm > 0 ? w / element.wMm : 1;
  const fy = element.hMm > 0 ? h / element.hMm : 1;
  const resized = { ...element, wMm: roundMm(w), hMm: roundMm(h) };
  switch (resized.kind) {
    case 'polyline':
      return { ...resized, pointsMm: resized.pointsMm.map(([x, y]): [number, number] => [roundMm(x * fx), roundMm(y * fy)]) };
    case 'table':
      return { ...resized, columns: resized.columns.map((c) => ({ ...c, widthMm: roundMm(c.widthMm * fx) })), rowHeightMm: roundMm(resized.rowHeightMm * fy) };
    case 'group':
      return {
        ...resized,
        children: resized.children.map((child) => {
          const cw = child.wMm * fx;
          const ch = child.hMm * fy;
          // The child's center moves with the group; its own box scales the same way.
          const cx = (child.xMm + child.wMm / 2) * fx;
          const cy = (child.yMm + child.hMm / 2) * fy;
          return { ...resizeElementBox(child, cw, ch), xMm: roundMm(cx - cw / 2), yMm: roundMm(cy - ch / 2) };
        }),
      };
    default:
      return resized;
  }
}

/** Inverse of a rigid 2D transform. */
export function invertMatrix(m: Matrix): Matrix {
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c || 1;
  return [d / det, -b / det, -c / det, a / det, (c * f - d * e) / det, (b * e - a * f) / det];
}

/** Whether `id` is `ancestorId` or lies inside it. */
function isWithin(spec: PrintTemplateSpec, id: string, ancestorId: string): boolean {
  if (id === ancestorId) return true;
  return findElement(spec, id)?.parents.some((p) => p.id === ancestorId) ?? false;
}

/** Shrink-wraps an unrotated group's box around its children (children keep their place on the page); drops it when empty. */
function fitGroup(spec: PrintTemplateSpec, groupId: string): PrintTemplateSpec {
  const location = findElement(spec, groupId);
  if (!location || location.element.kind !== 'group') return spec;
  const group = location.element;
  if (group.children.length === 0) return removeOnly(spec, [groupId]);
  if (group.rotationDeg) return spec;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const child of group.children) {
    const m = elementMatrix(child);
    for (const p of [apply(m, 0, 0), apply(m, child.wMm, 0), apply(m, child.wMm, child.hMm), apply(m, 0, child.hMm)]) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }
  return updateElement(spec, groupId, (g) => (g.kind === 'group' ? { ...g, xMm: g.xMm + minX, yMm: g.yMm + minY, wMm: maxX - minX, hMm: maxY - minY, children: g.children.map((c) => ({ ...c, xMm: c.xMm - minX, yMm: c.yMm - minY })) } : g));
}

/**
 * Moves an element to another place in the tree — a group (`parentId`) or
 * the page (null), at paint position `index` among the new siblings (0 =
 * bottom; counted without the moved element) — keeping where it is on the
 * page. A group can't move into itself. The groups it left / joined re-fit.
 */
export function moveElementTo(spec: PrintTemplateSpec, id: string, parentId: string | null, index: number): PrintTemplateSpec {
  const location = findElement(spec, id);
  if (!location) return spec;
  if (parentId && isWithin(spec, parentId, id)) return spec;
  const target = parentId ? findElement(spec, parentId) : null;
  if (parentId && (!target || target.element.kind !== 'group' || target.page.id !== location.page.id)) return spec;
  const element = location.element;
  // Where it is on the page: its center and total rotation.
  const center = apply(worldMatrix(location), element.wMm / 2, element.hMm / 2);
  const rotation = [...location.parents, element].reduce((sum, e) => sum + e.rotationDeg, 0);
  const newParents = target ? [...target.parents, target.element as GroupElement] : [];
  const local = apply(invertMatrix(parentMatrix(newParents)), center.x, center.y);
  const moved = { ...element, xMm: local.x - element.wMm / 2, yMm: local.y - element.hMm / 2, rotationDeg: normalizeDeg(rotation - newParents.reduce((sum, g) => sum + g.rotationDeg, 0)) };
  const oldParentId = location.parents[location.parents.length - 1]?.id ?? null;
  let next = removeOnly(spec, [id]);
  const place = (list: readonly PrintTemplateElement[]) => {
    const at = Math.max(0, Math.min(list.length, index));
    return [...list.slice(0, at), moved, ...list.slice(at)];
  };
  next = parentId ? updateElement(next, parentId, (g) => (g.kind === 'group' ? { ...g, children: place(g.children) } : g)) : mapPages(next, (page) => (page.id === location.page.id ? { ...page, elements: place(page.elements) } : page));
  if (parentId) next = fitGroup(next, parentId);
  if (oldParentId && oldParentId !== parentId) next = fitGroup(next, oldParentId);
  return next;
}

/** Dissolves a group: its children move to its parent, keeping where they are on the page. */
export function ungroup(spec: PrintTemplateSpec, groupId: string): { spec: PrintTemplateSpec; ids: string[] } {
  const location = findElement(spec, groupId);
  if (!location || location.element.kind !== 'group') return { spec, ids: [] };
  const group = location.element;
  const m = elementMatrix(group);
  const children = group.children.map((child) => {
    // The child's center through the group's transform; rotations add up.
    const center = apply(m, child.xMm + child.wMm / 2, child.yMm + child.hMm / 2);
    return { ...child, xMm: center.x - child.wMm / 2, yMm: center.y - child.hMm / 2, rotationDeg: normalizeDeg(child.rotationDeg + group.rotationDeg) };
  });
  const parentId = location.parents[location.parents.length - 1]?.id ?? null;
  let next = removeOnly(spec, [groupId]);
  for (const child of children) next = insertElement(next, location.page.id, child, parentId);
  return { spec: next, ids: children.map((c) => c.id) };
}

export function normalizeDeg(deg: number): number {
  const d = ((deg % 360) + 360) % 360;
  return d > 180 ? d - 360 : d;
}

// ─── Pages ──────────────────────────────────────────────────────────────

export function updatePage(spec: PrintTemplateSpec, pageId: string, update: (page: PrintPageSpec) => PrintPageSpec): PrintTemplateSpec {
  return mapPages(spec, (page) => (page.id === pageId ? update(page) : page));
}

export function addPage(spec: PrintTemplateSpec, afterPageId: string, name: string): { spec: PrintTemplateSpec; pageId: string } {
  const after = spec.pages.find((p) => p.id === afterPageId) ?? spec.pages[spec.pages.length - 1];
  const pageId = uniquePageId(spec);
  const page: PrintPageSpec = { id: pageId, name, paper: after.paper, orientation: after.orientation, marginsMm: after.marginsMm, repeat: 'none', elements: [] };
  const index = spec.pages.indexOf(after) + 1;
  return { spec: { ...spec, pages: [...spec.pages.slice(0, index), page, ...spec.pages.slice(index)] }, pageId };
}

export function duplicatePage(spec: PrintTemplateSpec, pageId: string, nameSuffix: string): { spec: PrintTemplateSpec; pageId: string } {
  const source = spec.pages.find((p) => p.id === pageId);
  if (!source) return { spec, pageId };
  const newId = uniquePageId(spec);
  const page: PrintPageSpec = { ...source, id: newId, name: `${source.name} ${nameSuffix}`.trim().slice(0, 200), elements: cloneWithNewIds(source.elements, allIds(spec)) };
  const index = spec.pages.indexOf(source) + 1;
  return { spec: { ...spec, pages: [...spec.pages.slice(0, index), page, ...spec.pages.slice(index)] }, pageId: newId };
}

export function removePage(spec: PrintTemplateSpec, pageId: string): PrintTemplateSpec {
  if (spec.pages.length <= 1) return spec;
  return { ...spec, pages: spec.pages.filter((p) => p.id !== pageId) };
}

export function movePage(spec: PrintTemplateSpec, pageId: string, move: 'up' | 'down'): PrintTemplateSpec {
  const pages = reorder(spec.pages, pageId, move === 'up' ? 'backward' : 'forward');
  return pages ? { ...spec, pages } : spec;
}

export function pageSize(page: PrintPageSpec): { widthMm: number; heightMm: number } {
  return pageSizeMm(page.paper, page.orientation);
}
